/**
 * The Spoils chest as a thing on the floor (BULLET-HEAVEN.md §H3).
 *
 * A boss dies and leaves this where it fell; walking over it opens the reel
 * (ui/SpoilsChest.js). The reward is decided at OPEN time, not here — this
 * object carries only where the chest is, which tier it is, and how far
 * through its landing arc it has got.
 *
 * It cannot be missed. After MAGNET_DELAY on the floor it comes to the player
 * on its own, for the same reason an XP mote past its lifetime vacuums rather
 * than expiring: a chest that could be walked past would mean a boss kill
 * silently paying nothing, and "I didn't see it" is never a fair way to lose a
 * reward the fight already earned.
 */

/** Seconds of upward-then-down arc before the chest settles. */
export const DROP_DURATION = 0.5;
/** World px the chest rises to at the apex of that arc. */
const DROP_HEIGHT = 22;
/** Seconds it lies still before homing in on the player. */
export const MAGNET_DELAY = 4;
/** px/sec once it does. Brisk — this is a delivery, not a chase. */
const MAGNET_SPEED = 190;

export class SpoilsChestDrop {
  /**
   * @param {object} opts
   * @param {number} opts.x - landing position (already clamped into the arena by the caller).
   * @param {number} opts.y
   * @param {string} opts.tier - see data/spoils.js chestTier.
   * @param {number} opts.stageIndex - the gauntlet index this chest was earned at.
   * @param {number} [opts.radius] - pickup radius; roughly the sprite's half-width.
   * @param {boolean} [opts.animate] - false under skipAnimations: lands instantly, no arc, no bob.
   */
  constructor({ x, y, tier = 'plain', stageIndex = 0, radius = 12, animate = true }) {
    this.x = x;
    this.y = y;
    this.tier = tier;
    this.stageIndex = stageIndex;
    this.radius = radius;
    this.animate = animate;
    this.active = true;
    this.age = 0;
    // Zero under skipAnimations, so `landed` is true from the first frame and
    // the chest is immediately collectible — the arc is presentation, and the
    // one thing this setting must never turn off is the reward.
    this.arcRemaining = animate ? DROP_DURATION : 0;
    this.magnetizing = false;
  }

  /** True once the arc has played out — a chest in mid-air isn't collectible yet. */
  get landed() {
    return this.arcRemaining <= 0;
  }

  /**
   * Height above its landing spot, in world px, for the renderer to offset by.
   * A parabola through the arc, then a slow bob once settled (0 when static).
   */
  liftAt(nowSeconds) {
    if (!this.landed) {
      // t runs 0 -> 1 across the arc; 4t(1-t) peaks at 1 halfway through.
      const t = 1 - this.arcRemaining / DROP_DURATION;
      return DROP_HEIGHT * 4 * t * (1 - t);
    }
    if (!this.animate) return 0;
    return 1.2 + Math.sin(nowSeconds * 2.4) * 1.2;
  }

  /**
   * @returns {boolean} true on the frame the player collects it.
   */
  update(dt, player) {
    if (!this.active) return false;
    this.age += dt;
    if (!this.landed) {
      this.arcRemaining -= dt;
      // Deliberately no collection check while airborne: a player standing on
      // the boss's corpse would otherwise grab it on frame one and never see
      // the drop at all.
      return false;
    }

    if (this.age >= MAGNET_DELAY) this.magnetizing = true;

    const dx = player.x - this.x;
    const dy = player.y - this.y;
    const dist = Math.hypot(dx, dy) || 1;
    if (this.magnetizing) {
      const step = Math.min(dist, MAGNET_SPEED * dt);
      this.x += (dx / dist) * step;
      this.y += (dy / dist) * step;
    }

    if (Math.hypot(player.x - this.x, player.y - this.y) < this.radius + player.radius) {
      this.active = false;
      return true;
    }
    return false;
  }
}
